import { useState } from 'react'
import { 
  FlaskConical, 
  Plus, 
  Clock,
  Zap,
  Shield,
  Star,
  CheckCircle,
  Lock,
  TrendingUp
} from 'lucide-react'
import { Button } from '../components/ui/button'

interface Technology {
  id: string
  name: string
  description: string
  category: 'military' | 'economic' | 'exploration' | 'defense'
  level: number
  cost: number
  duration: number // minutes
  prerequisites: string[]
  status: 'available' | 'researching' | 'completed' | 'locked'
  progress?: number
  timeRemaining?: string
}

const ResearchCenter = () => {
  const [technologies] = useState<Technology[]>([
    {
      id: '1',
      name: 'Plasma Cannons',
      description: 'Advanced energy weapons that deal devastating damage to enemy shields.',
      category: 'military',
      level: 2,
      cost: 1500,
      duration: 120,
      prerequisites: ['Energy Weapons I'],
      status: 'available'
    },
    {
      id: '2',
      name: 'Quantum Computing',
      description: 'Revolutionary computing technology that enhances all research speeds.',
      category: 'exploration',
      level: 3,
      cost: 2500,
      duration: 180,
      prerequisites: ['Advanced AI', 'Quantum Physics'],
      status: 'researching',
      progress: 65,
      timeRemaining: '45m'
    },
    {
      id: '3',
      name: 'Fusion Reactors',
      description: 'Clean, efficient energy production that powers advanced technologies.',
      category: 'economic',
      level: 2,
      cost: 2000,
      duration: 150,
      prerequisites: ['Nuclear Physics'],
      status: 'completed'
    },
    {
      id: '4',
      name: 'Adaptive Armor',
      description: 'Self-repairing armor systems that adapt to incoming damage types.',
      category: 'defense',
      level: 4,
      cost: 3500,
      duration: 240,
      prerequisites: ['Smart Materials', 'Nanotechnology'],
      status: 'locked'
    }
  ])

  const [activeTab, setActiveTab] = useState('all')
  const [researchQueue] = useState([
    { name: 'Quantum Computing', timeRemaining: '45m' },
    { name: 'Warp Drive III', timeRemaining: '2h 15m' }
  ])

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'military': return Zap
      case 'defense': return Shield
      case 'economic': return Star
      case 'exploration': return FlaskConical
      default: return FlaskConical
    }
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'military': return 'text-red-400 bg-red-600/20'
      case 'defense': return 'text-blue-400 bg-blue-600/20'
      case 'economic': return 'text-yellow-400 bg-yellow-600/20'
      case 'exploration': return 'text-purple-400 bg-purple-600/20'
      default: return 'text-gray-400 bg-gray-600/20'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'text-green-400 bg-green-600/20'
      case 'researching': return 'text-blue-400 bg-blue-600/20'
      case 'completed': return 'text-purple-400 bg-purple-600/20'
      case 'locked': return 'text-gray-400 bg-gray-600/20'
      default: return 'text-gray-400 bg-gray-600/20'
    }
  }

  const filteredTechnologies = technologies.filter(tech => 
    activeTab === 'all' || tech.category === activeTab
  )

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-900 to-slate-900 pt-16">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2 flex items-center">
              <FlaskConical className="w-10 h-10 mr-3 text-purple-400" />
              Research Center
            </h1>
            <p className="text-gray-400">Advance your civilization through scientific discovery</p>
          </div>
          <Button className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Queue Research
          </Button>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <FlaskConical className="w-6 h-6 text-purple-400" />
              <span className="text-xs text-gray-400">RESEARCH POINTS</span>
            </div>
            <div className="text-2xl font-bold text-white">2,450</div>
            <div className="text-xs text-green-400 flex items-center mt-1">
              <TrendingUp className="w-3 h-3 mr-1" />
              +120/hour
            </div>
          </div>
          
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="w-6 h-6 text-green-400" />
              <span className="text-xs text-gray-400">COMPLETED</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {technologies.filter(t => t.status === 'completed').length}
            </div>
          </div>
          
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-6 h-6 text-blue-400" />
              <span className="text-xs text-gray-400">IN PROGRESS</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {technologies.filter(t => t.status === 'researching').length}
            </div>
          </div>
          
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <Star className="w-6 h-6 text-yellow-400" />
              <span className="text-xs text-gray-400">AVAILABLE</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {technologies.filter(t => t.status === 'available').length}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Research Area */}
          <div className="lg:col-span-2">
            {/* Category Tabs */}
            <div className="flex flex-wrap gap-2 mb-6">
              {['all', 'military', 'defense', 'economic', 'exploration'].map((tab) => (
                <Button
                  key={tab}
                  variant={activeTab === tab ? 'default' : 'outline'}
                  onClick={() => setActiveTab(tab)}
                  className="capitalize"
                >
                  {tab === 'all' ? 'All Technologies' : tab}
                </Button>
              ))}
            </div>

            {/* Technologies Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredTechnologies.map((tech) => {
                const CategoryIcon = getCategoryIcon(tech.category)
                return (
                  <div key={tech.id} className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-white/10 p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getCategoryColor(tech.category)}`}>
                          <CategoryIcon className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-white">{tech.name}</h3>
                          <div className="flex items-center space-x-2 text-sm">
                            <span className="text-gray-400">Level {tech.level}</span>
                            <span className={`px-2 py-1 rounded text-xs capitalize ${getStatusColor(tech.status)}`}>
                              {tech.status}
                            </span>
                          </div>
                        </div>
                      </div>
                      {tech.status === 'locked' && <Lock className="w-5 h-5 text-gray-500" />}
                    </div>

                    <p className="text-gray-300 mb-4 text-sm">{tech.description}</p>

                    {/* Progress Bar for Researching */}
                    {tech.status === 'researching' && tech.progress !== undefined && (
                      <div className="mb-4">
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-gray-400">Progress</span>
                          <span className="text-blue-400">{tech.progress}%</span>
                        </div>
                        <div className="w-full bg-slate-700 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${tech.progress}%` }}
                          ></div>
                        </div>
                        {tech.timeRemaining && (
                          <div className="text-xs text-gray-400 mt-1">
                            Time remaining: {tech.timeRemaining}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Prerequisites */}
                    {tech.prerequisites.length > 0 && (
                      <div className="mb-4">
                        <div className="text-xs text-gray-400 mb-2">Prerequisites:</div>
                        <div className="flex flex-wrap gap-1">
                          {tech.prerequisites.map((prereq, index) => (
                            <span key={index} className="px-2 py-1 bg-slate-700/50 text-xs text-gray-300 rounded">
                              {prereq}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Cost and Duration */}
                    <div className="flex justify-between items-center mb-4 text-sm">
                      <div className="text-gray-400">
                        Cost: <span className="text-yellow-400">{tech.cost} RP</span>
                      </div>
                      <div className="text-gray-400">
                        Duration: <span className="text-blue-400">{tech.duration}m</span>
                      </div>
                    </div>

                    {/* Action Button */}
                    <Button 
                      className="w-full" 
                      disabled={tech.status === 'researching' || tech.status === 'completed' || tech.status === 'locked'}
                      variant={tech.status === 'available' ? 'default' : 'secondary'}
                    >
                      {tech.status === 'available' && 'Start Research'}
                      {tech.status === 'researching' && 'Researching...'}
                      {tech.status === 'completed' && 'Completed'}
                      {tech.status === 'locked' && 'Locked'}
                    </Button>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Research Queue Sidebar */}
          <div className="space-y-6">
            {/* Current Research */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-white/10 p-6">
              <h3 className="text-lg font-bold text-white mb-4">Research Queue</h3>
              
              {researchQueue.length > 0 ? (
                <div className="space-y-3">
                  {researchQueue.map((item, index) => (
                    <div key={index} className={`p-3 rounded-lg ${index === 0 ? 'bg-blue-600/20 border border-blue-500/30' : 'bg-slate-700/30'}`}>
                      <div className="flex justify-between items-center">
                        <span className="text-white text-sm font-medium">{item.name}</span>
                        <span className="text-xs text-gray-400">{item.timeRemaining}</span>
                      </div>
                      {index === 0 && (
                        <div className="mt-2">
                          <div className="w-full bg-slate-700 rounded-full h-1">
                            <div className="bg-blue-500 h-1 rounded-full" style={{ width: '65%' }}></div>
                          </div>
                        </div>
                      )}
                      <div className="text-xs text-gray-400 mt-1">
                        {index === 0 ? 'Currently researching' : `Position ${index + 1} in queue`}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <FlaskConical className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400 text-sm">No research in progress</p>
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 rounded-lg p-6 border border-purple-500/30">
              <h3 className="text-lg font-semibold text-white mb-4">Research Overview</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-300">Total Technologies</span>
                  <span className="text-white font-semibold">{technologies.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Completion Rate</span>
                  <span className="text-green-400 font-semibold">
                    {Math.round((technologies.filter(t => t.status === 'completed').length / technologies.length) * 100)}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Research Speed</span>
                  <span className="text-blue-400 font-semibold">+25%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Queue Efficiency</span>
                  <span className="text-purple-400 font-semibold">Optimal</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ResearchCenter